import { businessType, jobPositions } from '@forwardflow/helpers/utils'
import { states } from '@forwardflow/helpers/utils/states'
import useSetupProgress from '@forwardflow/hooks/customHooks/onboarding/useSetupProgress'
import BusinessService from '@forwardflow/services/BusinessService'
import { useA2PRegistrationModalStore } from '@forwardflow/store/modal'
import { Button, SelectField, TextField } from '@forwardflow/ui'
import Icon from '@forwardflow/ui/Icons'
import { Form, Modal } from 'antd'
import { useEffect, useState } from 'react'
import useAuthUser from '@forwardflow/hooks/customHooks/useAuthUser'

const RegistrationCompleted = () => {
   return (
      <div className="flex flex-col gap-y-5">
         <div className="w-[100px] h-[100px] rounded-full bg-primary/40 flexc mx-auto mt-[110px]">
            <Icon name="Check" className="scale-[5] fill-white" />
         </div>
         <p className="text-[24px] font-semibold text-center">
            Sent to the carriers
         </p>
         <p className="mb-20 text-sm text-center text-white/60 max-w-[360px] mx-auto">
            Approval usually takes 3 to 5 days. Texts switch on by themselves
            once it clears. Nothing more to do here.
         </p>
      </div>
   )
}
export const A2PRegistrationModal = () => {
   const { isA2PRegistrationModalOpen, closeA2PRegistrationModal } =
      useA2PRegistrationModalStore()
   const [isRegistrationCompleted, setRegistrationCompleted] = useState(false)
   const { data: progressDetails, mutate: refreshProgress } = useSetupProgress()
   const [form] = Form.useForm()
   const [isLoading, setIsLoading] = useState(false)
   const jobPosition = Form.useWatch(
      'authorizedRepresentativeJobPosition',
      form
   )
   const { data: user } = useAuthUser({})

   // Pre-fill from what the account already knows: saved business details
   // first, then the signed-in user for anything still blank.
   useEffect(() => {
      if (!isA2PRegistrationModalOpen) return
      const saved = progressDetails?.businessDetails
      const derived = {
         ...(saved || {}),
         email: saved?.email || user?.email,
         authorizedRepresentativeEmail:
            saved?.authorizedRepresentativeEmail || user?.email,
         authorizedRepresentativeFirstName:
            saved?.authorizedRepresentativeFirstName || user?.firstName,
         authorizedRepresentativeLastName:
            saved?.authorizedRepresentativeLastName || user?.lastName,
         friendlyBusinessName:
            saved?.friendlyBusinessName || saved?.legalBusinessName,
      }
      const filled = Object.fromEntries(
         Object.entries(derived).filter(([, v]) => v !== undefined && v !== null && v !== '')
      )
      form.setFieldsValue(filled)
   }, [isA2PRegistrationModalOpen, progressDetails, user, form])

   const handleSubmit = async () => {
      setIsLoading(true)
      try {
         await form.validateFields()
         await BusinessService.updateBusinessDetails(form.getFieldsValue())
         setRegistrationCompleted(true)
         // The home page reads the A2P status from here; show "in review" right away.
         void refreshProgress()
      } catch (error) {
         console.error('Form validation error:', error)
      } finally {
         setIsLoading(false)
      }
   }
   return (
      <>
         {isRegistrationCompleted ? (
            <Modal
               className="ff-modal"
               title=""
               open={isA2PRegistrationModalOpen}
               onCancel={closeA2PRegistrationModal}
               footer={null}
            >
               <RegistrationCompleted />
            </Modal>
         ) : (
            <Modal
               className="p-0 ff-modal"
               title=""
               open={isA2PRegistrationModalOpen}
               onCancel={closeA2PRegistrationModal}
               footer={null}
            >
               <div className="border-b border-white/20 px-11 pt-[30px] pb-5 mb-5">
                  <p className="text-[22px] font-medium mb-4">
                     Turn on texting
                  </p>
                  <p className="text-[#818181] text-[12px] font-medium ">
                     Carriers need to know who is texting. Confirm these details once and we file the registration for you. Approval usually takes 3 to 5 days; texts switch on by themselves after that.
                  </p>
               </div>

               <section className="px-10 pt-0 pb-8">
                  <Form
                     layout="vertical"
                     form={form}
                     className="text-left text-white"
                     autoComplete="off"
                     initialValues={progressDetails?.businessDetails}
                     onFinish={handleSubmit}
                  >
                     <div className="max-h-[50vh] overflow-y-auto">
                        <TextField
                           name="email"
                           isRequired
                           type="email"
                           errorMessage="Please enter email"
                           placeholder="Email"
                           label="Email"
                           validateEmail
                        />
                        <TextField
                           name="phoneNumber"
                           isRequired
                           errorMessage="Please enter phone number"
                           placeholder="Mobile Number"
                           label="Mobile Number"
                           validatePhoneNumber
                        />
                        <TextField
                           name="legalBusinessName"
                           isRequired
                           errorMessage="Please enter legal business name"
                           placeholder="Legal Business Name"
                           label="Legal Business Name"
                        />
                        <TextField
                           name="friendlyBusinessName"
                           isRequired
                           errorMessage="Please enter friendly business name"
                           placeholder="Friendly Business Name"
                           label="Friendly Business Name"
                        />
                        <TextField
                           name="socialMediaProfileUrls"
                           isRequired
                           errorMessage="Please enter social media profile URL/s"
                           placeholder="Social media profile URL/s"
                           label="Social media profile URL/s"
                           validateUrl
                           tooltip="Please enter URLs separated by comma"
                        />
                        <TextField
                           name="websiteUrl"
                           isRequired
                           errorMessage="Please enter website url"
                           placeholder="Website URL"
                           label="Website URL"
                           validateUrl
                        />
                        <SelectField
                           name="businessType"
                           placeholder="Business Type"
                           label="Business Type"
                           isRequired
                           errorMessage="Please select business type"
                           options={businessType}
                           tooltip="The legal structure of the business"
                        />
                        <TextField
                           name="businessRegistrationNumber"
                           isRequired
                           errorMessage="Please enter business registration number"
                           placeholder="9-digit EIN"
                           label="Business EIN"
                           validateRegistrationNumber
                        />

                        <div className="grid grid-cols-2 gap-4">
                           <TextField
                              name="authorizedRepresentativeFirstName"
                              isRequired
                              errorMessage="Please enter your first name"
                              placeholder="First name"
                              label="First name"
                              tooltip="The first name of the business authorized representative"
                           />
                           <TextField
                              name="authorizedRepresentativeLastName"
                              isRequired
                              errorMessage="Please enter your last name"
                              placeholder="Last name"
                              label="Last name"
                              tooltip="The last name of the business authorized representative"
                           />
                        </div>

                        <div
                           className={`grid ${
                              jobPosition === 'Other'
                                 ? 'grid-cols-2'
                                 : 'grid-cols-1'
                           } gap-4`}
                        >
                           <SelectField
                              name="authorizedRepresentativeJobPosition"
                              isRequired
                              errorMessage="Please select job position"
                              options={jobPositions}
                              placeholder="Job position"
                              label="Job position"
                           />
                           {jobPosition === 'Other' && (
                              <TextField
                                 name="authorizedRepresentativeBusinessTitle"
                                 isRequired
                                 errorMessage="Please enter business title"
                                 placeholder="Business Title"
                                 label="Business Title"
                                 tooltip="The exact job title of the business authorized representative"
                              />
                           )}
                        </div>
                        <TextField
                           name="authorizedRepresentativePhoneNumber"
                           isRequired
                           errorMessage="Please enter mobile phone number"
                           placeholder="Mobile Phone Number"
                           label="Mobile Phone Number"
                           validatePhoneNumber
                        />
                        <TextField
                           name="authorizedRepresentativeEmail"
                           isRequired
                           type="email"
                           errorMessage="Please enter email"
                           placeholder="Email"
                           label="Email"
                           validateEmail
                        />

                        <div className="grid grid-cols-2 gap-4">
                           <TextField
                              name="street"
                              isRequired
                              errorMessage="Please enter street"
                              placeholder="Street"
                              label="Street"
                           />
                           <TextField
                              name="postalCode"
                              isRequired
                              errorMessage="Please enter postal code"
                              placeholder="Postal code"
                              label="Postal code"
                              validatePostalCode
                           />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                           <TextField
                              name="city"
                              isRequired
                              errorMessage="Please enter city"
                              placeholder="City"
                              label="City"
                           />

                           <SelectField
                              name="region"
                              isRequired
                              errorMessage="Please select region"
                              options={states}
                              placeholder="Region"
                              label="Region"
                           />
                        </div>
                     </div>

                     <Button
                        isLoading={isLoading}
                        isDisabled={isLoading}
                        className="w-full !mt-5 text-white"
                        type="submit"
                     >
                        Send for carrier approval
                     </Button>
                  </Form>
               </section>
            </Modal>
         )}
      </>
   )
}
